import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import {
  Archive,
  ArchiveRestore,
  Check,
  Inbox,
  Loader2,
  MapPin,
  Minus,
  Phone,
  Search,
  Video,
  X,
} from "lucide-react";
import {
  Avatar,
  ClientName,
  clientInitials,
  DataTable,
  EmptyState,
  LeadStateBadge,
  PageHeader,
  Pagination,
  TriageControl,
  type Column,
} from "../components/ui";
import { useToastPush } from "../components/ui/Toast";
import { GET_LEADS_LIST, GET_LEAD_IDS, ARCHIVE_LEADS } from "../apollo/leads";
import { GET_BOOKING_TYPES } from "../apollo/sessions";
import { formatWhen, methodToLocation, timeAgo } from "../utils/sessionTime";

const PER_PAGE = 50;
const DEBOUNCE_MS = 300;

// ── Types for the query shape ────────────────────────────────────────

interface LeadCustomerRow {
  id: string;
  name: string;
  alias: string | null;
  email: string | null;
  isPrimary: boolean;
}

interface LeadRow {
  id: string;
  status: string;
  source: string | null;
  triageStatus: string;
  situation: string | null;
  method: string | null;
  bookingTypeId: string | null;
  archivedAt: string | null;
  createdAt: string;
  customers: LeadCustomerRow[];
}

interface BookingTypeRow {
  id: string;
  name: string;
}

// ── Helpers ──────────────────────────────────────────────────────────

type FilterKey = "all" | "new" | "qualified" | "booked" | "archived";

const DEFAULT_FILTER: FilterKey = "all";

function readFilter(params: URLSearchParams): FilterKey {
  const v = params.get("filter");
  if (v === "all" || v === "new" || v === "qualified" || v === "booked" || v === "archived")
    return v;
  return DEFAULT_FILTER;
}

function readPage(params: URLSearchParams): number {
  const v = params.get("page");
  if (v != null) {
    const n = parseInt(v, 10);
    if (!Number.isNaN(n) && n >= 0) return n;
  }
  return 0;
}

function primaryCustomer(lead: LeadRow): LeadCustomerRow | null {
  return lead.customers.find((c) => c.isPrimary) ?? lead.customers[0] ?? null;
}

function MethodIcon({ method }: { method: string }) {
  if (method === "in-person") return <MapPin size={12} strokeWidth={2} />;
  if (method === "video") return <Video size={12} strokeWidth={2} />;
  if (method === "phone") return <Phone size={12} strokeWidth={2} />;
  return null;
}

// ── Component ────────────────────────────────────────────────────────

export function Leads() {
  const navigate = useNavigate();
  const toast = useToastPush();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Derive list state from URL search params ────────────────────
  const filterKey = readFilter(searchParams);
  const page = readPage(searchParams);
  const typeFilter = searchParams.get("type") ?? "all";
  const debouncedSearch = searchParams.get("q") ?? "";

  // searchInput is local state — the live typing buffer. Seeded from the
  // URL's "q" param so a restored URL shows the query.
  const [searchInput, setSearchInput] = useState(() => searchParams.get("q") ?? "");

  // Whether we're on the archived view (determines archive vs unarchive label)
  const isArchivedView = filterKey === "archived";

  // Keep the latest setSearchParams in a ref so URL writers stay
  // referentially stable (same pattern as Clients — prevents effect
  // cascades tied to setSearchParams identity).
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  });

  // ── URL writers ─────────────────────────────────────────────────

  const setTypeFilter = useCallback((value: string) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      if (value === "all") next.delete("type");
      else next.set("type", value);
      next.delete("page");
      return next;
    });
  }, []);

  const setPage = useCallback((p: number) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      if (p === 0) next.delete("page");
      else next.set("page", String(p));
      return next;
    });
  }, []);

  // Debounce search input: write "q" to URL after DEBOUNCE_MS, resetting
  // the page. Skips the write when the trimmed input already matches the
  // URL — avoids resetting ?page= on mount when restoring a URL.
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchParamsRef.current((prev) => {
        const currentQ = prev.get("q") ?? "";
        if (trimmed === currentQ) return prev;
        const next = new URLSearchParams(prev);
        if (trimmed) next.set("q", trimmed);
        else next.delete("q");
        next.delete("page");
        return next;
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Query ───────────────────────────────────────────────────────

  const filter = useMemo(() => {
    const f: Record<string, unknown> = { archived: filterKey === "archived" };
    // status/bookingTypeId are auto-generated StringFilter inputs — pass the
    // { eq } operator form (same as reflex-admin's leads inspector).
    if (filterKey !== "all" && filterKey !== "archived") f.status = { eq: filterKey };
    if (typeFilter !== "all") f.bookingTypeId = { eq: typeFilter };
    if (debouncedSearch) f.q = debouncedSearch;
    return f;
  }, [filterKey, typeFilter, debouncedSearch]);

  const { data, loading, error, previousData, refetch } = useQuery(GET_LEADS_LIST, {
    variables: {
      page,
      perPage: PER_PAGE,
      sortField: "created_at",
      sortOrder: "DESC",
      filter,
    },
    fetchPolicy: "cache-and-network",
  });

  // Only fall back to previousData when paginating within the same
  // filter/search context; on context change show the skeleton instead
  // of stale rows (same pattern as Clients).
  const variablesKey = `${filterKey}|${typeFilter}|${debouncedSearch}`;
  const prevKeyRef = useRef(variablesKey);
  const contextChanged = variablesKey !== prevKeyRef.current;
  useEffect(() => {
    if (data) {
      prevKeyRef.current = variablesKey;
    }
  }, [data, variablesKey]);

  const effectiveData = data ?? (contextChanged ? undefined : previousData);
  const isStale = loading && effectiveData != null && data == null;
  const leads: LeadRow[] = effectiveData?.allLeads ?? [];
  const totalCount: number = effectiveData?._allLeadsMeta?.count ?? 0;

  const { data: typeData } = useQuery(GET_BOOKING_TYPES, {
    variables: { perPage: 1000, sortField: "name", sortOrder: "ASC" },
  });
  const bookingTypes: BookingTypeRow[] = typeData?.allBookingTypes ?? [];

  const typeMap = useMemo(() => {
    const m = new Map<string, string>();
    bookingTypes.forEach((t) => m.set(t.id, t.name));
    return m;
  }, [bookingTypes]);

  // ── Bulk selection ──────────────────────────────────────────────
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<{
    action: "archive" | "unarchive";
    count: number;
  } | null>(null);

  const [archiveLeadsMut, { loading: archiveLoading }] = useMutation(ARCHIVE_LEADS);
  const [fetchAllIds] = useLazyQuery(GET_LEAD_IDS, { fetchPolicy: "network-only" });

  const selectionCount = selectAllMatching ? totalCount : checkedIds.size;
  const hasSelection = selectionCount > 0;

  // Page-level IDs for select-all-on-page
  const pageIds = useMemo(() => new Set(leads.map((l) => l.id)), [leads]);
  const allOnPageChecked =
    pageIds.size > 0 && Array.from(pageIds).every((id) => checkedIds.has(id));
  const someOnPageChecked =
    !allOnPageChecked &&
    pageIds.size > 0 &&
    Array.from(pageIds).some((id) => checkedIds.has(id));

  // Anchor for shift-click range selection: the id of the last row toggled
  // with a plain (non-shift) click.
  const checkAnchorRef = useRef<string | null>(null);

  const handleRowCheck = useCallback(
    (id: string, shiftKey: boolean) => {
      const anchorId = checkAnchorRef.current;
      if (shiftKey && anchorId && anchorId !== id) {
        const anchorIdx = leads.findIndex((l) => l.id === anchorId);
        const clickedIdx = leads.findIndex((l) => l.id === id);
        if (anchorIdx !== -1 && clickedIdx !== -1) {
          const [lo, hi] =
            anchorIdx < clickedIdx ? [anchorIdx, clickedIdx] : [clickedIdx, anchorIdx];
          const rangeIds = leads.slice(lo, hi + 1).map((l) => l.id);
          setSelectAllMatching(false);
          setCheckedIds((prev) => {
            const next = new Set(prev);
            for (const rid of rangeIds) next.add(rid);
            return next;
          });
          return;
        }
      }
      // Plain click: toggle this row and make it the new range anchor.
      setSelectAllMatching(false);
      setCheckedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      checkAnchorRef.current = id;
    },
    [leads],
  );

  const toggleAllOnPage = useCallback(() => {
    setSelectAllMatching(false);
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageChecked) {
        for (const id of pageIds) next.delete(id);
      } else {
        for (const id of pageIds) next.add(id);
      }
      return next;
    });
  }, [allOnPageChecked, pageIds]);

  const clearBulkSelection = useCallback(() => {
    setCheckedIds(new Set());
    setSelectAllMatching(false);
    checkAnchorRef.current = null;
  }, []);

  // Changing the result-set context (tab, type, or search — but not the
  // page) clears the selection, so a bulk action never targets rows that
  // are no longer in the visible set. Skip the initial mount so restored
  // URLs don't churn state.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    clearBulkSelection();
    setArchiveConfirm(null);
  }, [filterKey, typeFilter, debouncedSearch, clearBulkSelection]);

  /** Resolve the IDs a bulk action targets. For select-all-matching,
   *  fetch every matching ID with the current filter. */
  const resolveSelectedIds = useCallback(async (): Promise<string[]> => {
    if (!selectAllMatching) {
      return Array.from(checkedIds);
    }
    const { data: idsData } = await fetchAllIds({
      variables: { filter, perPage: Math.max(totalCount, PER_PAGE) },
    });
    const all: { id: string }[] = idsData?.allLeads ?? [];
    return all.map((l) => l.id);
  }, [selectAllMatching, checkedIds, fetchAllIds, filter, totalCount]);

  const handleBulkArchive = useCallback(
    async (archive: boolean) => {
      setArchiveConfirm(null);
      try {
        const ids = await resolveSelectedIds();
        if (ids.length === 0) return;

        await archiveLeadsMut({ variables: { ids, archive } });

        await refetch();
        clearBulkSelection();
        const verb = archive ? "archived" : "unarchived";
        toast({
          kind: "success",
          title: `${ids.length} ${ids.length === 1 ? "lead" : "leads"} ${verb}`,
        });
      } catch {
        toast({
          kind: "error",
          title: `Failed to ${archive ? "archive" : "unarchive"} leads`,
          description: "Something went wrong. Please try again.",
        });
      }
    },
    [resolveSelectedIds, archiveLeadsMut, refetch, clearBulkSelection, toast],
  );

  // ── Checkbox column ─────────────────────────────────────────────

  const CheckboxHeader = (
    <button
      type="button"
      onClick={toggleAllOnPage}
      className="w-4 h-4 rounded border hair flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer bg-paper-hi hover:bg-paper-2"
      aria-label={allOnPageChecked ? "Deselect all on this page" : "Select all on this page"}
    >
      {allOnPageChecked ? (
        <Check size={10} strokeWidth={3} className="text-tide" />
      ) : someOnPageChecked ? (
        <Minus size={10} strokeWidth={3} className="text-tide" />
      ) : null}
    </button>
  );

  // Columns
  const columns: Column<LeadRow>[] = [
    {
      key: "check",
      header: CheckboxHeader,
      colSpan: 1,
      render: (l) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRowCheck(l.id, e.shiftKey);
          }}
          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
            checkedIds.has(l.id)
              ? "bg-tide border-tide"
              : "hair bg-paper-hi hover:bg-paper-2"
          }`}
          aria-label={`${checkedIds.has(l.id) ? "Deselect" : "Select"} ${primaryCustomer(l)?.name ?? "lead"}`}
        >
          {checkedIds.has(l.id) && (
            <Check size={10} strokeWidth={3} className="text-paper-hi" />
          )}
        </button>
      ),
    },
    {
      key: "received",
      header: "Received",
      colSpan: 2,
      render: (l) => {
        // createdAt is a naive-UTC ISO timestamp; split into the (date, time)
        // parts the sessionTime helpers expect.
        const [date = "", time = ""] = l.createdAt.split("T");
        return (
          <div>
            <div className="text-[13px] font-medium num">{formatWhen(date, time)}</div>
            <div className="text-[11px] text-ink-4">{timeAgo(l.createdAt)}</div>
          </div>
        );
      },
    },
    {
      key: "contact",
      header: "Contact",
      colSpan: 2,
      render: (l) => {
        const customer = primaryCustomer(l);
        if (!customer) {
          return <div className="text-[13px] text-ink-4 italic">No contact yet</div>;
        }
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar initials={clientInitials(customer)} />
            <div className="min-w-0">
              <div className="text-[13px] font-medium truncate">
                <ClientName customer={customer} />
                {l.customers.length > 1 && (
                  <span className="text-ink-4 font-normal"> +{l.customers.length - 1}</span>
                )}
              </div>
              {customer.email && (
                <div className="text-[11px] text-ink-4 truncate">{customer.email}</div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "consultation",
      header: "Consultation",
      colSpan: 2,
      hideBelow: "lg",
      render: (l) => (
        <div>
          <div className="text-[12.5px] text-ink-3 truncate">
            {l.bookingTypeId ? (typeMap.get(l.bookingTypeId) ?? "—") : "—"}
          </div>
          {l.method && (
            <div className="text-[11px] text-ink-4 flex items-center gap-1 mt-0.5">
              <MethodIcon method={l.method} />
              {methodToLocation(l.method)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "source",
      header: "Source",
      colSpan: 1,
      hideBelow: "lg",
      render: (l) => (
        <span className="text-[11.5px] text-ink-3">
          {l.source === "lead_form" ? "Form" : l.source === "booking_funnel" ? "Funnel" : l.source ?? "—"}
        </span>
      ),
    },
    {
      key: "triage",
      header: "Triage",
      colSpan: 2,
      render: (l) => <TriageControl leadId={l.id} value={l.triageStatus} />,
    },
    {
      key: "status",
      header: "Status",
      colSpan: 2,
      align: "right",
      render: (l) => (
        <div className="flex justify-end">
          <LeadStateBadge status={l.status} />
        </div>
      ),
    },
  ];

  const tableFooter = (
    <Pagination
      page={page}
      perPage={PER_PAGE}
      total={totalCount}
      onPageChange={setPage}
      unit="lead"
    />
  );

  const summaryText =
    loading && leads.length === 0
      ? "Loading leads..."
      : debouncedSearch
        ? `${totalCount} lead${totalCount !== 1 ? "s" : ""} matching “${debouncedSearch}”.`
        : isArchivedView
          ? `${totalCount} archived lead${totalCount !== 1 ? "s" : ""}.`
          : totalCount > 0
            ? `${totalCount} enquir${totalCount !== 1 ? "ies" : "y"} from your website.`
            : "Enquiries from your website will appear here.";

  return (
    <div>
      <PageHeader
        tabsLabel="Lead views"
        tabs={[
          { key: "all", label: "All" },
          { key: "new", label: "New" },
          { key: "qualified", label: "Qualified" },
          { key: "booked", label: "Booked" },
          { key: "archived", label: "Archived" },
        ]}
        activeTab={filterKey}
        tabParam="filter"
        defaultTab={DEFAULT_FILTER}
        status={summaryText}
        controls={
          <>
            <div className="relative flex-1 min-w-[160px] max-w-xs">
              <Search
                size={13}
                strokeWidth={2}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4 pointer-events-none"
              />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search leads..."
                aria-label="Search leads by contact or situation"
                className="w-full pl-8 pr-3 min-h-11 md:min-h-0 md:py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink placeholder:text-ink-4 focus:outline-none focus:border-tide/50"
              />
            </div>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              aria-label="Filter by consultation type"
              className="px-3 min-h-11 md:min-h-0 md:py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 hover:bg-paper-2 appearance-none cursor-pointer pr-7 flex-shrink-0"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23516680' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 8px center",
              }}
            >
              <option value="all">All consultation types</option>
              {bookingTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </>
        }
      />

      {/* Selection toolbar */}
      {hasSelection && (
        <div className="mx-4 md:mx-7 mb-3 px-4 py-2.5 bg-tide-ss rounded-lg hair border border-tide/30 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-[13px]">
            <span className="font-medium text-ink">
              {selectionCount} {selectionCount === 1 ? "lead" : "leads"} selected
            </span>
            {allOnPageChecked && !selectAllMatching && totalCount > pageIds.size && (
              <button
                type="button"
                onClick={() => setSelectAllMatching(true)}
                className="text-tide hover:underline text-[12.5px]"
              >
                Select all {totalCount} matching
              </button>
            )}
            {selectAllMatching && (
              <span className="text-ink-3 text-[12.5px]">
                All {totalCount} matching leads are selected.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                setArchiveConfirm({
                  action: isArchivedView ? "unarchive" : "archive",
                  count: selectionCount,
                })
              }
              disabled={archiveLoading}
              className="px-3 py-1.5 rounded-md bg-paper-hi text-ink-2 border hair hover:bg-paper-2 text-[12.5px] flex items-center gap-1.5 transition-colors disabled:opacity-50"
            >
              {archiveLoading ? (
                <Loader2 size={12} strokeWidth={1.8} className="animate-spin" />
              ) : isArchivedView ? (
                <ArchiveRestore size={12} strokeWidth={1.8} />
              ) : (
                <Archive size={12} strokeWidth={1.8} />
              )}
              {isArchivedView ? "Unarchive" : "Archive"}
            </button>
            <button
              type="button"
              onClick={clearBulkSelection}
              className="w-6 h-6 rounded hover:bg-paper-2 flex items-center justify-center text-ink-4"
              aria-label="Clear selection"
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}

      {/* Archive confirmation dialog */}
      {archiveConfirm && (
        <div className="mx-4 md:mx-7 mb-3 px-4 py-3 bg-paper-hi rounded-lg shadow-card hair border flex items-center justify-between gap-3">
          <div className="text-[13px] text-ink">
            {archiveConfirm.action === "archive" ? (
              <>
                Archive {archiveConfirm.count}{" "}
                {archiveConfirm.count === 1 ? "lead" : "leads"}? They will be
                hidden from your active leads list.
              </>
            ) : (
              <>
                Unarchive {archiveConfirm.count}{" "}
                {archiveConfirm.count === 1 ? "lead" : "leads"}? They will
                reappear in your active leads list.
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setArchiveConfirm(null)}
              className="px-3 py-1.5 rounded-md text-ink-3 hover:bg-paper-2 text-[12.5px] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleBulkArchive(archiveConfirm.action === "archive")}
              disabled={archiveLoading}
              className="px-3 py-1.5 rounded-md bg-ink text-paper-hi text-[12.5px] font-medium hover:bg-ink/90 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              {archiveLoading && (
                <Loader2 size={12} strokeWidth={2} className="animate-spin" />
              )}
              {archiveConfirm.action === "archive" ? "Archive" : "Unarchive"}
            </button>
          </div>
        </div>
      )}

      {/* Table body */}
      <div className="px-4 md:px-7 pb-20">
        {loading && leads.length === 0 ? (
          <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
            {/* Skeleton */}
            <div className="grid grid-cols-12 gap-3 px-5 py-2.5 hair-b bg-wash text-[11px] font-semibold text-ink-4 uppercase tracking-wider">
              <div className="col-span-1" />
              <div className="col-span-2">Received</div>
              <div className="col-span-2">Contact</div>
              <div className="col-span-2 hidden lg:block">Consultation</div>
              <div className="col-span-1 hidden lg:block">Source</div>
              <div className="col-span-2">Triage</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            <div className="divide-hair">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 px-5 py-4 items-center">
                  <div className="col-span-1">
                    <div className="h-4 w-4 rounded bg-canvas animate-pulse" />
                  </div>
                  <div className="col-span-2">
                    <div className="h-3.5 w-16 rounded bg-canvas animate-pulse" />
                    <div className="h-2.5 w-20 rounded bg-canvas animate-pulse mt-1.5" />
                  </div>
                  <div className="col-span-2">
                    <div className="h-3.5 w-28 rounded bg-canvas animate-pulse" />
                  </div>
                  <div className="col-span-2 hidden lg:block">
                    <div className="h-3 w-24 rounded bg-canvas animate-pulse" />
                  </div>
                  <div className="col-span-1 hidden lg:block">
                    <div className="h-3 w-10 rounded bg-canvas animate-pulse" />
                  </div>
                  <div className="col-span-2">
                    <div className="h-5 w-20 rounded-full bg-canvas animate-pulse" />
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <div className="h-4 w-16 rounded-full bg-canvas animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="bg-paper-hi rounded-lg shadow-card">
            <EmptyState
              icon={<Inbox size={28} strokeWidth={1.5} />}
              heading="Failed to load leads"
              description="Something went wrong. Please try refreshing the page."
            />
          </div>
        ) : leads.length > 0 ? (
          <div
            className={`transition-opacity ${isStale ? "opacity-60 pointer-events-none" : ""}`}
          >
            <DataTable<LeadRow>
              columns={columns}
              data={leads}
              footer={tableFooter}
              onRowClick={(l) => navigate(`/leads/${l.id}`)}
              rowHighlight={isArchivedView ? () => "opacity-70" : undefined}
            />
          </div>
        ) : (
          <div className="bg-paper-hi rounded-lg shadow-card">
            <EmptyState
              icon={<Inbox size={28} strokeWidth={1.5} />}
              heading={
                debouncedSearch
                  ? "No leads match your search"
                  : isArchivedView
                    ? "No archived leads"
                    : filterKey === "all" && typeFilter === "all"
                      ? "No leads yet"
                      : "No leads match these filters"
              }
              description={
                debouncedSearch
                  ? "Try a different name, email, or keyword."
                  : isArchivedView
                    ? "Leads you archive will appear here."
                    : filterKey === "all" && typeFilter === "all"
                      ? "Leads will appear here as people enquire through your website."
                      : "Try a different status or consultation type."
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
